import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { SeoHead } from '@/components/SeoHead';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import {
  MCP_PAGE_URL,
  MCP_SNAPSHOT,
  PUBLIC_OPENAPI_URL,
  installClients,
  type McpTool,
} from '@/content/mcp';

const SEO_TITLE = 'Serwer MCP Bookeper – kalkulatory podatkowe JDG dla asystentów AI';
const SEO_DESCRIPTION =
  'Publiczny serwer MCP z kalkulatorami Bookeper dla JDG: ZUS, składka zdrowotna, ryczałt, forma opodatkowania, IP Box. Bez logowania, tylko do odczytu.';

const SECTIONS = [
  { id: 'adres', heading: 'Adres serwera' },
  { id: 'instalacja', heading: 'Jak dodać serwer do asystenta AI' },
  { id: 'narzedzia', heading: `Narzędzia (${MCP_SNAPSHOT.tools.length})` },
  { id: 'cytowanie', heading: 'Cytowanie, założenia i zastrzeżenie' },
  { id: 'zakres', heading: 'Zakres i ograniczenia' },
  { id: 'rest', heading: 'To samo przez REST API' },
] as const;

const sectionHeading = (id: (typeof SECTIONS)[number]['id']) =>
  SECTIONS.find((section) => section.id === id)?.heading;

function CodeBlock({ label, children }: { label?: string; children: string }) {
  return (
    <figure className="mt-3">
      {label ? (
        <figcaption className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </figcaption>
      ) : null}
      <pre className="overflow-x-auto rounded-lg border border-border/60 bg-muted/60 p-4 text-sm leading-relaxed text-foreground">
        <code>{children}</code>
      </pre>
    </figure>
  );
}

function ToolCard({ tool }: { tool: McpTool }) {
  const required = tool.parameters.filter((parameter) => parameter.required);
  const optionalCount = tool.parameters.length - required.length;
  return (
    <article
      id={`narzedzie-${tool.name}`}
      className="rounded-xl border border-border/60 bg-card p-5"
      data-testid="mcp-tool"
    >
      <h3 className="text-lg font-semibold text-foreground">{tool.title}</h3>
      <p className="mt-1 font-mono text-sm text-primary">{tool.name}</p>
      <p className="mt-3 text-sm text-foreground/80 leading-relaxed" lang="en">
        {tool.description}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Wymagane parametry:</span>{' '}
        {required.length > 0 ? (
          required.map((parameter, index) => (
            <span key={parameter.name}>
              {index > 0 ? ', ' : null}
              <code className="font-mono">{parameter.name}</code>
            </span>
          ))
        ) : (
          <span>brak</span>
        )}
        {optionalCount > 0 ? <span>{` · opcjonalnych: ${optionalCount}`}</span> : null}
      </p>
    </article>
  );
}

/**
 * www.bookeper.pl/mcp — the human install page of the public MCP server
 * (BOO-5261 U5, spec §B.11). Polish only: the /en and /uk routes render this
 * page and canonicalise here, like the /przewodniki explainers.
 */
const Mcp = () => {
  const clients = installClients();
  return (
    <div className="min-h-screen pt-16 md:pt-20">
      <SeoHead>
        <html lang="pl" />
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={MCP_PAGE_URL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:url" content={MCP_PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pl_PL" />
      </SeoHead>

      <Navigation />

      <article className="bg-background">
        <nav
          aria-label="Ścieżka nawigacji"
          className="max-w-3xl mx-auto px-6 md:px-8 pt-10 text-sm text-muted-foreground"
        >
          <ol className="flex flex-wrap items-center gap-1.5">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">
                Bookeper
              </Link>
            </li>
            <li aria-hidden="true" role="presentation">
              <ChevronRight className="size-3.5" />
            </li>
            <li aria-current="page" className="text-foreground">
              Serwer MCP
            </li>
          </ol>
        </nav>

        <header className="max-w-3xl mx-auto px-6 md:px-8 pt-6 pb-10">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
            Serwer MCP Bookeper: kalkulatory podatkowe JDG dla asystentów AI
          </h1>
          <p className="text-lg md:text-xl text-foreground/80 leading-relaxed">
            Serwer MCP Bookeper udostępnia asystentom AI, takim jak Claude, ChatGPT czy Cursor, te
            same kalkulatory, które działają na stronie{' '}
            <a href="/kalkulatory" className="text-primary underline-offset-4 hover:underline">
              www.bookeper.pl/kalkulatory
            </a>
            : składki ZUS, składkę zdrowotną, ryczałt, porównanie form opodatkowania, IP Box i
            parametry podatkowe roku. Jest publiczny i tylko do odczytu: nie wymaga logowania ani
            klucza API.
          </p>
        </header>

        <nav aria-label="Spis treści" className="max-w-3xl mx-auto px-6 md:px-8 pb-12">
          <div className="rounded-xl border border-border/60 bg-card p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Na tej stronie
            </h2>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-foreground hover:text-primary transition-colors"
                  >
                    {section.heading}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-6 md:px-8 pb-16 space-y-12">
          <section id="adres" aria-labelledby="adres-heading">
            <h2
              id="adres-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              {sectionHeading('adres')}
            </h2>
            <CodeBlock>{MCP_SNAPSHOT.url}</CodeBlock>
            <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="font-semibold text-foreground">Transport</dt>
                <dd className="text-foreground/80">Streamable HTTP ({MCP_SNAPSHOT.transport})</dd>
              </div>
              <div>
                <dt className="font-semibold text-foreground">Uwierzytelnianie</dt>
                <dd className="text-foreground/80">brak: bez logowania i bez klucza API</dd>
              </div>
              <div>
                <dt className="font-semibold text-foreground">Dostęp</dt>
                <dd className="text-foreground/80">
                  tylko do odczytu, każde narzędzie wyłącznie liczy
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-foreground">Wersja</dt>
                <dd className="text-foreground/80">{MCP_SNAPSHOT.version}</dd>
              </div>
            </dl>
          </section>

          <section id="instalacja" aria-labelledby="instalacja-heading">
            <h2
              id="instalacja-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              {sectionHeading('instalacja')}
            </h2>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
              Każdy klient potrzebuje tylko adresu serwera. Nazwy menu w aplikacjach zmieniają się
              między wersjami; jeśli nie znajdziesz opcji, szukaj ustawień „MCP”, „konektory” albo
              „connectors”.
            </p>
            <div className="mt-6 space-y-8">
              {clients.map((client) => (
                <div key={client.id} id={`instalacja-${client.id}`}>
                  <h3 className="text-xl font-semibold tracking-tight mb-2">{client.name}</h3>
                  <div className="space-y-2 text-base text-foreground/80 leading-relaxed">
                    {client.steps.map((step) => (
                      <p key={step}>{step}</p>
                    ))}
                  </div>
                  {client.snippet ? (
                    <CodeBlock label={client.snippetLabel}>{client.snippet}</CodeBlock>
                  ) : null}
                </div>
              ))}
            </div>
          </section>

          <section id="narzedzia" aria-labelledby="narzedzia-heading">
            <h2
              id="narzedzia-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              {sectionHeading('narzedzia')}
            </h2>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
              Lista pochodzi z odpowiedzi serwera na zapytanie tools/list, zapisanej przy budowie
              tej strony. Opisy są po angielsku, dokładnie tak, jak widzi je asystent AI.
            </p>
            <div className="mt-6 space-y-4">
              {MCP_SNAPSHOT.tools.map((tool) => (
                <ToolCard key={tool.name} tool={tool} />
              ))}
            </div>
          </section>

          <section id="cytowanie" aria-labelledby="cytowanie-heading">
            <h2
              id="cytowanie-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              {sectionHeading('cytowanie')}
            </h2>
            <div className="space-y-4 text-base md:text-lg text-foreground/80 leading-relaxed">
              <p>
                Każdy wynik zawiera podstawę prawną z numerem artykułu ustawy, założenia przyjęte w
                obliczeniu, gotowy tekst cytowania i link do kalkulatora na www.bookeper.pl. Serwer
                prosi asystenta AI, by przekazał je użytkownikowi razem z wynikiem.
              </p>
              <p>Każdy wynik niesie też to zastrzeżenie:</p>
            </div>
            <blockquote className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-5 text-base text-foreground">
              {MCP_SNAPSHOT.disclaimer}
            </blockquote>
          </section>

          <section id="zakres" aria-labelledby="zakres-heading">
            <h2
              id="zakres-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              {sectionHeading('zakres')}
            </h2>
            <ul className="space-y-2.5 list-disc pl-5 text-base text-foreground/80 leading-relaxed">
              <li>
                Tylko jednoosobowa działalność gospodarcza (JDG). Spółki, np. sp. z o.o., nie są
                obsługiwane.
              </li>
              <li>
                Wynik jest symulacją na danych, które poda asystent. Serwer tylko liczy: niczego nie
                składa i niczego nie wysyła do urzędów.
              </li>
              <li>
                Żadne narzędzie nie pyta o dane identyfikujące, takie jak imię i nazwisko, PESEL czy
                NIP.
              </li>
              <li>
                Gdy brakuje danych, narzędzie odmawia zamiast zgadywać i nazywa brakujące fakty, np.
                stawkę ryczałtu.
              </li>
            </ul>
          </section>

          <section id="rest" aria-labelledby="rest-heading">
            <h2
              id="rest-heading"
              className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
            >
              {sectionHeading('rest')}
            </h2>
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
              Te same obliczenia są dostępne przez zwykłe HTTPS (JSON na wejściu i na wyjściu), dla
              kodu, który nie korzysta z MCP. Opis wszystkich endpointów w formacie OpenAPI 3.1:
            </p>
            <CodeBlock>{PUBLIC_OPENAPI_URL}</CodeBlock>
          </section>

          <Link
            to="/"
            className="group flex items-center justify-between p-5 rounded-xl border border-border/50 bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300"
          >
            <span className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
              Wróć na stronę główną Bookeper
            </span>
            <ArrowRight
              className="size-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 ml-4"
              aria-hidden="true"
            />
          </Link>
        </div>
      </article>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Mcp;
